import { SelectItem } from "@mantine/core/lib/Select/types";
import { useDisclosure } from "@mantine/hooks";
import { useCallback, useMemo } from "react";

import { useCreateCreditUnderwriting } from "../../../api/hooks/creditUnderwriting/useCreateCreditUnderwriting.ts";
import { useCreditUnderwriting } from "../../../api/hooks/creditUnderwriting/useCreditUnderwriting.ts";
import { useUpdateCreditUnderwriting } from "../../../api/hooks/creditUnderwriting/useUpdateCreditUnderwriting.ts";
import { useHandoverLimitRequestToCU } from "../../../api/hooks/limitRequest/useHandoverLimitRequestToCU.ts";
import { useHandoverLimitRequestToCUManager } from "../../../api/hooks/limitRequest/useHandoverLimitRequestToCUManager.ts";
import { useHandoverLimitRequestToRR } from "../../../api/hooks/limitRequest/useHandoverLimitRequestToRR.ts";
import { useRejectLimitRequestByCUManager } from "../../../api/hooks/limitRequest/useRejectLimitRequestByCUManager.ts";
import { useCUAssignees } from "../../../api/hooks/user/useCUAssignees.ts";
import MultiFieldCard from "../../../components/Cards/MultiFieldCard/MultiFieldCard.tsx";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal.tsx";
import ModalWithNotes from "../../../components/ModalWithNotes/ModalWithNotes.tsx";
import {
  LimitRequestStatus,
  RolePermission,
} from "../../../graphql/generated.ts";
import { useMutationNotificationWrapper } from "../../../hooks/useMutationNotificationWrapper.tsx";
import { useCurrentUserContext } from "../../../providers/CurrentUserProvider.tsx";
import { LimitRequestDetail } from "../../../types/limitRequest/limitRequest.ts";
import { getUserOptions } from "../../../utils/user.ts";
import { hasPermission } from "../../../utils/user.ts";
import LimitRequestDetailCardControl from "../LimitRequestDetailCardControl/LimitRequestDetailCardControl.tsx";
import CreditUnderwritingModal, {
  CreditUnderwritingModalSubmitValue,
} from "./CreditUnderwritingModal.tsx";
import useCreditUnderwritingCardData from "./hooks/useCreditUnderwritingCardData.tsx";
import useCreditUnderwritingCardFooter from "./hooks/useCreditUnderwritingCardFooter.tsx";

interface Props {
  limitRequest: LimitRequestDetail | null | undefined;
  onRefreshLimitRequest: () => void;
}

export default function CreditUnderwritingCard({
  limitRequest,
  onRefreshLimitRequest,
}: Props) {
  const id = limitRequest?.creditUnderwriting?.id;
  const [{ data: response, fetching }, refresh] = useCreditUnderwriting({
    id,
  });
  const creditUnderwriting = response?.creditUnderwriting;

  const [{ data: assigneesData }] = useCUAssignees();
  const assigneesOptions: SelectItem[] = useMemo(() => {
    return getUserOptions(assigneesData?.users.data);
  }, [assigneesData]);

  const { user } = useCurrentUserContext();

  const [editModalOpened, { open: openEditModal, close: closeEditModal }] =
    useDisclosure(false);
  const [
    handoverToCUModalOpened,
    { open: openHandoverToCUModal, close: closeHandoverToCUModal },
  ] = useDisclosure(false);
  const [
    showRejectConfirmationModal,
    { open: openRejectConfirmationModal, close: closeRejectConfirmationModal },
  ] = useDisclosure(false);

  const [, updateCreditUnderwriting] = useMutationNotificationWrapper(
    useUpdateCreditUnderwriting(),
    {
      success: {
        message: "Credit Underwriting data was successfully updated.",
      },
    }
  );

  const [, createCreditUnderwriting] = useMutationNotificationWrapper(
    useCreateCreditUnderwriting(),
    {
      success: {
        message: "Credit Underwriting data was successfully added.",
      },
    }
  );

  const [, handoverLimitRequestToRR] = useMutationNotificationWrapper(
    useHandoverLimitRequestToRR(),
    {
      success: {
        message: "Limit Request was successfully handovered to Risk Review.",
      },
    }
  );

  const [, handoverLimitRequestToCUManager] = useMutationNotificationWrapper(
    useHandoverLimitRequestToCUManager(),
    {
      success: {
        message: "Limit Request was successfully handovered to CU Manager.",
      },
    }
  );

  const [, handoverLimitRequestToCU] = useMutationNotificationWrapper(
    useHandoverLimitRequestToCU(),
    {
      success: {
        message:
          "Limit Request was successfully handovered back to CU Officer.",
      },
    }
  );

  const [, rejectLimitRequestByCUManager] = useMutationNotificationWrapper(
    useRejectLimitRequestByCUManager(),
    {
      success: {
        message: "Limit Request was successfully rejected.",
      },
    }
  );

  const hasEditAccess = hasPermission(
    user,
    RolePermission.CanEditBuyerInfoLimitInfoCreditUnderwriting
  );

  const handleCreditUnderwritingSubmit = (
    assignedId: number | undefined,
    assignedDate: Date | undefined,
    input: CreditUnderwritingModalSubmitValue
  ) => {
    if (!limitRequest) {
      return;
    }

    if (id) {
      updateCreditUnderwriting({ id, assignedId, assignedDate, input })
        .then(() => onRefreshLimitRequest())
        .then(() => {
          refresh();
        });
    } else {
      createCreditUnderwriting({
        assignedId,
        assignedDate,
        input: { ...input, limitRequestId: limitRequest.id },
      })
        .then(() => onRefreshLimitRequest())
        .then(() => {
          refresh();
        });
    }
    closeEditModal();
  };

  const handleHandoverToRR = useCallback(() => {
    if (!limitRequest) {
      return;
    }
    handoverLimitRequestToRR({ id: limitRequest.id }).then((result) => {
      if (!result.error) {
        onRefreshLimitRequest();
      }
    });
  }, [handoverLimitRequestToRR, limitRequest, onRefreshLimitRequest]);

  const handleHandoverToCUManager = useCallback(() => {
    if (!limitRequest) {
      return;
    }
    handoverLimitRequestToCUManager({ id: limitRequest.id }).then((result) => {
      if (!result.error) {
        onRefreshLimitRequest();
      }
    });
  }, [handoverLimitRequestToCUManager, limitRequest, onRefreshLimitRequest]);

  const handleRejectByCUManager = useCallback(() => {
    if (!limitRequest) {
      return;
    }
    rejectLimitRequestByCUManager({ id: limitRequest.id })
      .then((result) => {
        if (!result.error) {
          onRefreshLimitRequest();
        }
      })
      .finally(closeRejectConfirmationModal);
  }, [
    closeRejectConfirmationModal,
    limitRequest,
    onRefreshLimitRequest,
    rejectLimitRequestByCUManager,
  ]);

  const handleHandoverToCU = useCallback(
    (note: string) => {
      if (!limitRequest || !creditUnderwriting) {
        return;
      }
      handoverLimitRequestToCU({
        id: limitRequest?.id,
        input: { text: note, creditUnderwritingId: creditUnderwriting.id },
      }).then(() => {
        onRefreshLimitRequest();
      });
    },
    [
      limitRequest,
      creditUnderwriting,
      handoverLimitRequestToCU,
      onRefreshLimitRequest,
    ]
  );

  const cardInfo = useCreditUnderwritingCardData({
    limitRequest,
    creditUnderwriting,
  });

  const cardControl = useMemo(() => {
    if (!creditUnderwriting) {
      return null;
    }
    return (
      <LimitRequestDetailCardControl
        updatedBy={creditUnderwriting?.updatedBy}
        edit={
          hasEditAccess &&
          creditUnderwriting &&
          limitRequest?.requestStatus === LimitRequestStatus.OngoingCu
        }
        onEdit={openEditModal}
      />
    );
  }, [creditUnderwriting, hasEditAccess, limitRequest, openEditModal]);

  const cardFooter = useCreditUnderwritingCardFooter({
    limitRequest,
    creditUnderwriting,
    onAddData: openEditModal,
    onHandoverToRR: handleHandoverToRR,
    onHandoverToCUManager: handleHandoverToCUManager,
    onHandoverToCU: openHandoverToCUModal,
    onRejectByCUManager: openRejectConfirmationModal,
  });

  return (
    <>
      <MultiFieldCard
        label="Credit Underwriting"
        variant="blue"
        cols={3}
        loading={fetching || !limitRequest}
        items={cardInfo}
        cardControl={cardControl}
        cardFooterContent={cardFooter}
      />
      {editModalOpened && (
        <CreditUnderwritingModal
          value={creditUnderwriting}
          limitRequest={limitRequest}
          opened={true}
          assigneesOptions={assigneesOptions}
          onClose={closeEditModal}
          onSubmit={handleCreditUnderwritingSubmit}
        />
      )}
      {showRejectConfirmationModal && (
        <ConfirmationModal
          opened={true}
          message={
            "Are you sure you want to Reject this limit? This process is Irreversible."
          }
          onConfirm={handleRejectByCUManager}
          onClose={closeRejectConfirmationModal}
        />
      )}
      <ModalWithNotes
        title="Handover to CU Officer"
        opened={handoverToCUModalOpened}
        onClose={closeHandoverToCUModal}
        onSubmit={handleHandoverToCU}
      />
    </>
  );
}
